import { Fragment } from 'react'

import { cn } from '@/lib/utils'

import { type StatusSegment, statusLineSegments, type Tone } from './status-line-segments'
import { useStatusLine } from './use-status-line'

const TONE_CLASS: Record<Tone, string> = {
  ok: 'text-success',
  attn: 'text-warning',
}

function Segment({ segment }: { segment: StatusSegment }) {
  return (
    <span className="whitespace-nowrap">
      {segment.map((part, index) => (
        <span className={part.tone && TONE_CLASS[part.tone]} key={index}>
          {part.text}
        </span>
      ))}
    </span>
  )
}

function Readout() {
  const { data, isError } = useStatusLine()
  // A failed background refetch keeps the last good reading on screen.
  if (!data) {
    return isError ? (
      <span className="text-warning">status unavailable</span>
    ) : (
      <span className="text-muted-foreground">checking…</span>
    )
  }
  return statusLineSegments(data, new Date()).map((segment, index) => (
    <Fragment key={index}>
      {index > 0 && <span className="text-muted-foreground/60"> · </span>}
      <Segment segment={segment} />
    </Fragment>
  ))
}

// One line of monospace text, first on Activity and Access, that says whether
// everything is fine.
export function StatusLine() {
  return (
    <div
      className={cn(
        'flex flex-wrap items-baseline justify-between gap-4 rounded-lg border bg-card px-3.5 py-[11px]',
        'font-mono text-[13px] text-foreground leading-[1.6] md:flex-nowrap'
      )}
      data-testid="status-line"
    >
      <span className="min-w-0">
        <span className="text-primary">$</span> <Readout />
      </span>
    </div>
  )
}
